import * as React from "react"
import { formatCost, formatTokens, formatDuration, projectDisplayName } from '@lens/lib/decode'
import type { ReplayData, SessionMeta } from '@lens/types/claude'
import { Badge } from "@excellent/ui"
import { Separator } from '@excellent/ui'
import { SectionHeading } from "@excellent/ui"
import { MiniBarRow } from '../../mini-bar-row'
import { GitBranch, Clock, FileCode2, Zap, Cpu } from "../../../../icons"

interface Props {
 replay: ReplayData
 meta?: SessionMeta
}

function SectionTitle({ children }: { children: React.ReactNode }) {
 return <SectionHeading as="h3" className="mb-3" title={children} />
}

export function SessionSidebar({ replay, meta }: Props) {
 let totalInput = 0,
 totalOutput = 0,
 totalCacheWrite = 0,
 totalCacheRead = 0
 for (const t of replay.turns) {
 if (t.usage) {
 totalInput += t.usage.input_tokens ?? 0
 totalOutput += t.usage.output_tokens ?? 0
 totalCacheWrite += t.usage.cache_creation_input_tokens ?? 0
 totalCacheRead += t.usage.cache_read_input_tokens ?? 0
 }
 }
 const totalTokens = totalInput + totalOutput + totalCacheWrite + totalCacheRead
 const pct = (n: number) => (totalTokens > 0 ? (n / totalTokens) * 100 : 0)

 const toolCounts = new Map<string, number>()
 for (const t of replay.turns) {
 for (const tc of t.tool_calls ?? []) {
 toolCounts.set(tc.name, (toolCounts.get(tc.name) ?? 0) + 1)
 }
 }
 const topTools = [...toolCounts.entries()]
 .sort((a, b) => b[1] - a[1])
 .slice(0, 8)
 const maxToolCount = topTools[0]?.[1] ?? 1

 const assistantTurns = replay.turns.filter(t => t.type === 'assistant')

 const tokenBreakdown = [
 { label: 'Input', val: totalInput, color: 'var(--viz-sky)', bg: 'bg-muted ' },
 { label: 'Output', val: totalOutput, color: 'var(--chart-4)', bg: 'bg-muted' },
 { label: 'Cache Write', val: totalCacheWrite, color: 'var(--chart-5)', bg: 'bg-muted' },
 { label: 'Cache Read', val: totalCacheRead, color: 'var(--chart-1)', bg: 'bg-muted' },
 ]

 const showTools = topTools.length > 0
 const showCompactions = replay.compactions.length > 0

 return (
 <div className="type-body">
 {/* Token breakdown */}
 <section>
 <SectionTitle>
 <span className="inline-flex items-center gap-1.5">
 <Cpu className="h-3.5 w-3.5" /> Token breakdown
 </span>
 </SectionTitle>
 <div className="space-y-3">
 {tokenBreakdown.map(({ label, val, color, bg }) => (
 <div key={label} className="space-y-1">
 <div className="flex items-center justify-between">
 <span className="type-body text-muted-foreground">{label}</span>
 <span className=" type-body " style={{ color }}>
 {formatTokens(val)}
 </span>
 </div>
 <div className="h-1.5 overflow-hidden rounded-lg bg-muted">
 <div
 className={`h-full rounded-lg ${bg} opacity-70 transition-all`}
 style={{ width: `${Math.max(2, pct(val))}%` }}
 />
 </div>
 </div>
 ))}
 <div className="flex items-center justify-between border-t border-border/50 pt-3">
 <span className="type-body text-muted-foreground">Total</span>
 <div className="flex items-center gap-2">
 <span className=" type-body text-foreground">{formatTokens(totalTokens)}</span>
 <span className=" type-body text-[var(--chart-4)]">{formatCost(replay.total_cost)}</span>
 </div>
 </div>
 </div>
 </section>

 {showTools && (
 <>
 <Separator className="my-5" />
 <section>
 <SectionTitle>Tools used</SectionTitle>
 <div className="space-y-2">
 {topTools.map(([name, count]) => {
 const shortName = name.startsWith('mcp__') ? name.split('__').slice(1).join(' · ') : name
 const width = Math.round((count / maxToolCount) * 100)
 return (
 <MiniBarRow
 key={name}
 label={<span title={name}>{shortName}</span>}
 value={count}
 width={width}
 color="rgb(from var(--chart-4) r g b / 0.6)"
 labelWidth="w-24"
 valueWidth="w-5"
 />
 )
 })}
 </div>
 </section>
 </>
 )}

 {showCompactions && (
 <>
 <Separator className="my-5" />
 <section>
 <SectionTitle>
 <span className="inline-flex items-center gap-1.5">
 <Zap className="h-3.5 w-3.5 text-foreground" /> Compactions
 </span>
 </SectionTitle>
 <div className="space-y-2.5">
 {replay.compactions.map(c => (
 <div
 key={c.uuid}
 className="flex items-start gap-2 rounded-lg surface-edge bg-muted px-2.5 py-2"
 >
 <Zap className="mt-0.5 h-3 w-3 shrink-0 text-foreground" />
 <div className="space-y-0.5">
 <div className="flex items-center gap-1.5">
 <span className="type-body text-foreground">Turn {c.turn_index}</span>
 <Badge
 variant="outline"
 className="h-4 border-border px-1 py-0 type-body text-foreground"
 >
 {c.trigger}
 </Badge>
 </div>
 <span className="type-body text-muted-foreground">{formatTokens(c.pre_tokens)} tok before</span>
 </div>
 </div>
 ))}
 </div>
 </section>
 </>
 )}

 <Separator className="my-5" />

 <section>
 <SectionTitle>Session info</SectionTitle>
 <div className="space-y-2">
 {replay.slug && (
 <div className="flex items-start gap-2">
 <span className="w-16 shrink-0 type-body text-muted-foreground">Slug</span>
 <span className="break-all type-body text-foreground">{replay.slug}</span>
 </div>
 )}
 {replay.version && (
 <div className="flex items-center gap-2">
 <span className="w-16 shrink-0 type-body text-muted-foreground">Version</span>
 <Badge variant="outline" className="h-4 px-1.5 py-0 type-body">
 v{replay.version}
 </Badge>
 </div>
 )}
 {replay.git_branch && (
 <div className="flex items-center gap-2">
 <span className="w-16 shrink-0 type-body text-muted-foreground">Branch</span>
 <div className="flex min-w-0 items-center gap-1">
 <GitBranch className="h-3 w-3 shrink-0 text-foreground" />
 <span className="truncate type-body text-foreground">{replay.git_branch}</span>
 </div>
 </div>
 )}
 <div className="flex items-center gap-2">
 <span className="w-16 shrink-0 type-body text-muted-foreground">Turns</span>
 <span className="type-body text-foreground">{assistantTurns.length}</span>
 </div>
 {meta && (
 <>
 {meta.duration_minutes > 0 && (
 <div className="flex items-center gap-2">
 <span className="w-16 shrink-0 type-body text-muted-foreground">Duration</span>
 <span className="flex items-center gap-1 type-body text-foreground">
 <Clock className="h-3 w-3 text-foreground" />
 {formatDuration(meta.duration_minutes)}
 </span>
 </div>
 )}
 {meta.project_path && (
 <div className="flex items-start gap-2">
 <span className="w-16 shrink-0 type-body text-muted-foreground">Project</span>
 <span className="truncate type-body text-foreground">{projectDisplayName(meta.project_path)}</span>
 </div>
 )}
 {(meta.lines_added ?? 0) > 0 && (
 <div className="flex items-center gap-2">
 <span className="w-16 shrink-0 type-body text-muted-foreground">Lines</span>
 <div className="flex items-center gap-1.5">
 <span className=" type-body text-foreground">+{meta.lines_added}</span>
 <span className=" type-body text-foreground">-{meta.lines_removed}</span>
 </div>
 </div>
 )}
 {meta.files_modified > 0 && (
 <div className="flex items-center gap-2">
 <span className="w-16 shrink-0 type-body text-muted-foreground">Files</span>
 <span className="flex items-center gap-1 type-body text-foreground">
 <FileCode2 className="h-3 w-3 text-foreground" />
 {meta.files_modified} modified
 </span>
 </div>
 )}
 </>
 )}
 </div>
 </section>
 </div>
 )
}
